import { MenuNode } from 'models/MenuNode';

import { MenuType } from '../config/Config';
import { ApiErrorData, ApiResponse } from '../models/ApiResponse';
import { Menu } from '../models/Menu';
import { FunctionsHelper } from '../utils/FunctionsHelper';
import MainBackendAxios from '../utils/MainBackendAxios';

export const MenuService = {
  getAll: async (): Promise<ApiResponse<Menu[] | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`/menu/get-all`);

      let menus: Menu[] = response?.data?.data;
      menus = menus.sort((a, b) => {
        return (a.companyColumnOrder || 0) - (b.companyColumnOrder || 0);
      });

      return {
        success: true,
        status: response.status,
        data: menus,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  createForCurrentCompany: async (
    body: CreateMenuForCurrentCompanyRequestDto
  ): Promise<ApiResponse<{ newMenuNode: MenuNode } | ApiErrorData>> => {
    console.log('create data', body);
    try {
      const response = await MainBackendAxios.post(
        `/menu/create-for-current-company`,
        body
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  deleteCompanyOwnerMenus: async (
    menuCodes: string[]
  ): Promise<ApiResponse<{ executedAction: boolean } | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.post(
        `/menu/delete/company-owner-menus`,
        { menuCodes }
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  reorganizeCompanyMenus: async (
    body: ReorganizeCompanyMenusRequestDto
  ): Promise<ApiResponse<{ executedAction: boolean } | ApiErrorData>> => {
    console.log('Reorganizate', body);
    try {
      const response = await MainBackendAxios.post(
        `/menu/reorganize-company-menus`,
        body
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  associateMenusWithRole: async (
    body: AssociateMenuWithRoleRequestDto
  ): Promise<ApiResponse<{ executedAction: boolean } | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.post(
        `/menu/associate-menus-with-role`,
        body
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  findByCode: async (
    code: string
  ): Promise<ApiResponse<Menu | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`/menu/find-by-code/${code}`);

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  updateForCurrentCompany: async (
    code: string,
    body: UpdateMenuForCurrentCompanyRequestDto
  ): Promise<ApiResponse<{ updatedNode: MenuNode } | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.put(
        `/menu/update-for-current-company/${code}`,
        body
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  associateMenusWithCompany: async (
    body: AssociateMenuWithCompanyRequestDto
  ): Promise<ApiResponse<{ executedAction: boolean } | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.post(
        `/menu/associate-menus-with-company`,
        body
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  getMenuNodesByCurrentCompany: async (): Promise<
    ApiResponse<MenuNode[] | ApiErrorData>
  > => {
    try {
      const response = await MainBackendAxios.get(
        `/menu/node/current-user/get-by-company`
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data || [],
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  getSystemMenuNodesWithCompanyAccess: async (
    companyId: number
  ): Promise<ApiResponse<MenuNode[] | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(
        `/menu/node/get-by-company/${companyId}`
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data as MenuNode[],
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  getSystemMenuNodesWithRoleAccess: async (
    roleId: number
  ): Promise<ApiResponse<MenuNode[] | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(
        `/menu/node/get-by-role/${roleId}`
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data as MenuNode[],
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
  validateEncryptedReportSecret: async (
    key: string,
    body: {
      secret: string;
    }
  ): Promise<ApiResponse<{ validated: boolean } | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.post(
        `/menu/report/validate-encrypted-secret/${key}`,
        body
      );

      return {
        success: true,
        status: response.status,
        data: response?.data?.data as { validated: boolean },
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
};

export interface CreateMenuForCurrentCompanyRequestDto {
  title: string;
  localizedTitles?: {
    es?: string | null;
    en?: string | null;
  } | null;
  menuType:
    | MenuType.PARENT_MENU
    | MenuType.IFRAME_VIEWER
    | MenuType.POWER_BI_REPORT
    | MenuType.PDF_VIEWER
    | MenuType.OFFICE_VIEWER;
  redirectLink: string | null;
  icon: string | null;
  parentMenuCode: string | null;
  additionalConfig: object;
}

export interface MenuReorganizeSchema {
  code: string;
  order: number;
  parentCode: string | null;
}

export interface ReorganizeCompanyMenusRequestDto {
  menus: MenuReorganizeSchema[];
}

export interface AssociateMenuWithRoleRequestDto {
  roleId: number;
  menus: {
    code: string;
    isRecursive: boolean;
  }[];
}

export interface AssociateMenuWithCompanyRequestDto {
  companyId: number;
  menus: string[];
}

export interface UpdateMenuForCurrentCompanyRequestDto {
  title: string;
  localizedTitles?: {
    es?: string | null;
    en?: string | null;
  } | null;
  redirectLink: string | null;
  icon: string | null;
  menuType?: MenuType;
  additionalConfig: object;
}
